import { routes } from '@/config/routes'
import { SELECTED_NETWORKS_KEY, SELECTED_TOKENS_KEY } from '@/constants/state'
import { useAccountContext } from '@/context/account'
import { useMixpanel } from '@/context/mixpanel'
import { useWalletType } from '@/hooks/useWalletType'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { formatUSD } from '@vaultsfyi/common/utils'
import { Link, generatePath } from 'react-router'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../common/accordion'
import { Button } from '../common/button'
import { Loader } from '../common/loader'
import { NetworkImage } from '../common/networkImage'
import { TokenImage } from '../common/tokenImage'
import { ArrowIcon } from '../icons/arrow'
import { ExternalLinkIcon } from '../icons/externalLink'
import { FundWalletDialog } from './fundWalletDialog'
import type { AssetsBalanceGroup } from './useGroupedBalances/useGroupedBalances'

interface Props {
  assetsBalance: AssetsBalanceGroup | null
  className?: string
}

interface HeaderProps {
  hideFunding: boolean | undefined
}

export const IdleAssetsHeader = ({ assetsBalance, className, hideFunding }: Props & HeaderProps) => {
  const totalBalance = assetsBalance?.reduce((acc, { groupTotalBalance }) => acc + groupTotalBalance, 0)
  const { account, chain } = useAccountContext()
  const { isEOA } = useWalletType(account)

  return (
    <div className={cn('flex flex-col gap-1 px-4 md:px-5', className)}>
      <div className="flex justify-between gap-2">
        <h6 className="font-medium text-base text-heading-foreground">Idle assets - not earning</h6>
        <p className="font-medium text-body-foreground tabular-nums">
          {totalBalance ? `${formatUSD(totalBalance, { notation: 'standard' })} total` : '––'}
        </p>
      </div>
      <p className="text-body-foreground text-sm">
        These tokens are sitting in your wallet without earning any interest. Deploy them to start earning.
      </p>
      {account !== undefined && chain && isEOA && !hideFunding && (
        <FundWalletDialog address={account} chain={chain} className="mt-2" />
      )}
    </div>
  )
}

export const IdleAssetsContent = ({ assetsBalance, className }: Props) => {
  const { trackEvent } = useMixpanel()

  if (!assetsBalance) {
    return (
      <div className="py-4">
        <Loader />
      </div>
    )
  }

  return (
    <Accordion type="multiple">
      <ul className={cn('flex flex-col gap-2 px-4 md:px-5', className)}>
        {assetsBalance.map(({ balances, groupTotalBalance, groupName }) => (
          <li key={groupName} data-highlight={groupName} className="overflow-hidden rounded-xl border bg-page">
            <AccordionItem value={groupName}>
              <AccordionTrigger asChild>
                <Button
                  variant="default"
                  size="default"
                  className="group flex w-full items-center gap-2 rounded-xl px-3 py-2 ring-inset"
                  onClick={() => {
                    trackEvent('Idle asset item expanded', { Group: groupName })
                  }}
                >
                  <TokenImage symbol={groupName} className="size-8 shrink-0" />
                  <span className="flex-1 text-left font-medium text-heading-foreground text-sm">{groupName}</span>
                  <p className="text-body-foreground text-sm tabular-nums">
                    {formatUSD(groupTotalBalance, { notation: 'standard' })}
                  </p>
                  <div className="group-data-[state=open]:-scale-y-100 shrink-0 transition-transform">
                    <ArrowIcon direction="down" />
                  </div>
                </Button>
              </AccordionTrigger>
              <AccordionContent className="border-t px-3">
                <ul className="flex flex-col gap-1 pt-1 pb-2 text-xs">
                  {balances.map(({ network, balanceUsd, symbol }) => {
                    const presetParams = new URLSearchParams({
                      [SELECTED_NETWORKS_KEY]: network.name,
                      [SELECTED_TOKENS_KEY]: symbol,
                    })
                    const presetUrl = `${generatePath(routes.root)}?${presetParams}`
                    return (
                      <li className="flex items-center justify-between" key={`${groupName}-${network.name}`}>
                        <Button
                          type="button"
                          size="default"
                          variant="default"
                          className="group flex items-center gap-2 rounded-full text-accent capitalize"
                          asChild
                        >
                          <Link to={presetUrl}>
                            <NetworkImage network={network.name} />
                            <div className="flex items-center gap-1">
                              <span className="underline">{getNetworkName(network.name)}</span>
                              <ExternalLinkIcon className="opacity-0 transition-opacity group-hocus:opacity-100" />
                            </div>
                          </Link>
                        </Button>
                        <p className="tabular-nums">{formatUSD(balanceUsd)}</p>
                      </li>
                    )
                  })}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </li>
        ))}
      </ul>
    </Accordion>
  )
}
